/**
 * 구독 2채널의 설정. 🔴 **숫자를 코드에 박지 않는다.**
 *
 * ## stale 임계 — 지어내지 않는다
 *
 * 계약(`ModuleSnapshot.stale`)이 못 박는다:
 *
 * > 임계 = `health_capability.heartbeat_interval_ms` × `health_capability.missed_beat_threshold`,
 * > 그 둘의 **전역 기본은 `module_service.openapi.yaml`** 이 갖는다. 모듈이 선언하면 그 값이 이긴다.
 *
 * ⚠ 계약은 같은 자리에 **실측 사고**를 기록해 뒀다 — 「구현이 찾지 못해 근거 없는 고정값을 넣었다
 * (2026-09-13)」. 우리는 그 사고를 반복하지 않는다.
 *
 * 🔴 **값이 없으면 `staleThresholdMs` 는 `undefined` 이고, 그러면 시간 기반 stale 판정을
 *    «하지 않는다».** 연결 상태(close·error) 기반 판정만 남는다 — 그쪽은 임계가 필요 없다.
 *    임의의 기본값(30초 따위)을 넣는 순간 근거 없는 판정이 장비 제어 버튼을 막거나 열게 된다.
 *
 * 실측값이 오면 `.env`(`VITE_HEARTBEAT_INTERVAL_MS` · `VITE_MISSED_BEAT_THRESHOLD`) 또는
 * `/config.js` 의 `realtime` 섹션에 넣는다. **코드는 고치지 않는다.**
 */
import { positiveNumber, positiveNumberFromText, runtimeSection } from './runtimeConfig.ts'

export interface ReconnectPolicy {
  /** 첫 재연결 대기(ms) */
  initialDelayMs: number
  /** 대기 상한(ms) */
  maxDelayMs: number
  /** 지수 배수 */
  factor: number
}

export interface RealtimeConfig {
  /**
   * 🔴 `undefined` = **시간 기반 stale 판정 없음**. 값이 실측되기 전까지의 정상 상태다.
   * 판정하지 않는 것과 「신선하다고 판정한 것」은 다르다 — 연결이 끊기면 그쪽 축이 막는다.
   */
  staleThresholdMs: number | undefined
  /**
   * 전송 계층 정책이다(계약 값이 아니다). 재연결 백오프는 서버 상태 판정과 무관하므로
   * 기본값을 둔다. 바꾸려면 `/config.js` 의 `realtime.reconnect`.
   */
  reconnect: ReconnectPolicy
  /**
   * `lastMessageAt` 을 상태에 반영하는 최소 간격(ms). 텔레메트리가 초당 수십 건 와도
   * 렌더가 그만큼 일어나지 않게 한다. ⚠ 폴링이 아니다 — 이벤트가 없으면 아무 일도 없다.
   */
  activityUpdateMs: number
}

const DEFAULT_RECONNECT: ReconnectPolicy = {
  initialDelayMs: 1_000,
  maxDelayMs: 30_000,
  factor: 2,
}

const DEFAULT_ACTIVITY_UPDATE_MS = 1_000

export interface StaleThresholdInput {
  heartbeatIntervalMs?: number | undefined
  missedBeatThreshold?: number | undefined
}

/** 🔴 둘 중 하나라도 없으면 `undefined`. 한쪽만 가지고 임계를 만들어 내지 않는다 */
export function staleThresholdMs(input: StaleThresholdInput): number | undefined {
  const interval = positiveNumber(input.heartbeatIntervalMs)
  const missed = positiveNumber(input.missedBeatThreshold)
  if (interval === undefined || missed === undefined) return undefined
  return interval * missed
}

export function resolveRealtimeConfig(input: {
  runtime?: Record<string, unknown> | undefined
  env?:
    | {
        heartbeatIntervalMs?: string | undefined
        missedBeatThreshold?: string | undefined
      }
    | undefined
}): RealtimeConfig {
  const runtime = input.runtime ?? {}
  const reconnectRaw = runtime['reconnect']
  const reconnect = isRecordLike(reconnectRaw) ? reconnectRaw : {}

  return {
    staleThresholdMs: staleThresholdMs({
      heartbeatIntervalMs:
        positiveNumber(runtime['heartbeatIntervalMs']) ??
        positiveNumberFromText(input.env?.heartbeatIntervalMs),
      missedBeatThreshold:
        positiveNumber(runtime['missedBeatThreshold']) ??
        positiveNumberFromText(input.env?.missedBeatThreshold),
    }),
    reconnect: {
      initialDelayMs:
        positiveNumber(reconnect['initialDelayMs']) ?? DEFAULT_RECONNECT.initialDelayMs,
      maxDelayMs: positiveNumber(reconnect['maxDelayMs']) ?? DEFAULT_RECONNECT.maxDelayMs,
      factor: positiveNumber(reconnect['factor']) ?? DEFAULT_RECONNECT.factor,
    },
    activityUpdateMs: positiveNumber(runtime['activityUpdateMs']) ?? DEFAULT_ACTIVITY_UPDATE_MS,
  }
}

function isRecordLike(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

let cached: RealtimeConfig | undefined

export function realtimeConfig(): RealtimeConfig {
  cached ??= resolveRealtimeConfig({
    runtime: runtimeSection('realtime'),
    env: {
      heartbeatIntervalMs: import.meta.env.VITE_HEARTBEAT_INTERVAL_MS,
      missedBeatThreshold: import.meta.env.VITE_MISSED_BEAT_THRESHOLD,
    },
  })
  return cached
}

/** 테스트 전용 */
export function resetRealtimeConfigCache(): void {
  cached = undefined
}
